import { useSyncExternalStore } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';

// Colecciones grandes compartidas entre pantallas (pedido de Mica, 2026-10-10:
// que las páginas carguen más rápido). Antes cada pantalla abría su propia
// lectura del catálogo entero (~8.500 productos) al entrar, aunque otra
// pantalla lo hubiera cargado recién. Ahora se lee UNA vez, queda en memoria
// y se mantiene al día en vivo; al pasar de una pantalla a otra los datos ya
// están. Si nadie los usa por un rato se sueltan, y al cerrar sesión se borran.
const MINUTOS_EN_MEMORIA = 15;
const ESPERA_SERVIDOR_MS = 10000;

const almacenes = [];

function crearAlmacen(nombreColeccion, campoId) {
  const vacio = () => ({ lista: [], cargando: true, delServidor: false, error: null });
  let porId = new Map();
  let estado = vacio();
  let cancelar = null;
  let cierre = null;
  const oyentes = new Set();
  const ordenadas = new Map(); // campo -> { de: lista original, lista ordenada }

  const avisar = () => oyentes.forEach((f) => f());

  function detener() {
    clearTimeout(cierre);
    cierre = null;
    if (cancelar) cancelar();
    cancelar = null;
    porId = new Map();
    ordenadas.clear();
    estado = vacio();
  }

  function iniciar() {
    clearTimeout(cierre);
    cierre = null;
    if (cancelar) return;
    estado = vacio();
    cancelar = onSnapshot(collection(db, nombreColeccion), { includeMetadataChanges: true }, (snap) => {
      const cambios = snap.docChanges();
      const delServidor = estado.delServidor || !snap.metadata.fromCache;
      if (cambios.length === 0 && !estado.cargando && delServidor === estado.delServidor) return;
      // Solo se vuelven a armar los documentos que cambiaron, no los miles
      // que siguen igual (antes cada venta rearmaba el catálogo entero).
      cambios.forEach((c) => {
        if (c.type === 'removed') porId.delete(c.doc.id);
        else porId.set(c.doc.id, { [campoId]: c.doc.id, ...c.doc.data() });
      });
      estado = {
        lista: cambios.length > 0 || estado.cargando ? Array.from(porId.values()) : estado.lista,
        cargando: false,
        delServidor,
        error: null,
      };
      avisar();
    }, (error) => {
      console.error(`No se pudo leer "${nombreColeccion}"`, error);
      detener();
      estado = { ...vacio(), cargando: false, error };
      avisar();
    });
  }

  function suscribir(f) {
    oyentes.add(f);
    iniciar();
    return () => {
      oyentes.delete(f);
      if (oyentes.size === 0 && cancelar) {
        clearTimeout(cierre);
        cierre = setTimeout(detener, MINUTOS_EN_MEMORIA * 60 * 1000);
      }
    };
  }

  const leer = () => estado;

  // Misma lista, ordenada por un campo (como hacía orderBy: los documentos
  // que no tienen ese campo quedan afuera). Se ordena una sola vez por cambio.
  function ordenadaPor(campo) {
    const guardada = ordenadas.get(campo);
    if (guardada && guardada.de === estado.lista) return guardada.lista;
    const lista = estado.lista
      .filter((x) => x[campo] !== undefined)
      .sort((a, b) => (a[campo] < b[campo] ? -1 : a[campo] > b[campo] ? 1 : 0));
    ordenadas.set(campo, { de: estado.lista, lista });
    return lista;
  }

  // Para las pantallas que necesitan una foto de los datos una sola vez
  // (reemplaza a getDocs de la colección entera): espera a que estén al día
  // con el servidor. Sin conexión, pasado un rato usa la copia guardada.
  function unaVez() {
    return new Promise((resolve, reject) => {
      let terminado = false;
      let vencido = false;
      let baja = null;
      let reloj = null;
      const revisar = () => {
        if (terminado) return;
        if (!estado.error && !estado.delServidor && !(vencido && !estado.cargando)) return;
        terminado = true;
        clearTimeout(reloj);
        const { error, lista } = estado;
        if (baja) baja(); else setTimeout(() => baja && baja(), 0);
        if (error) reject(error); else resolve(lista);
      };
      reloj = setTimeout(() => { vencido = true; revisar(); }, ESPERA_SERVIDOR_MS);
      baja = suscribir(revisar);
      revisar();
    });
  }

  const almacen = { suscribir, leer, ordenadaPor, unaVez, detener, avisar };
  almacenes.push(almacen);
  return almacen;
}

const almacenProductos = crearAlmacen('productos', 'sku');
const almacenPublicacionesML = crearAlmacen('publicaciones_mercado_libre', 'id');
const almacenClientes = crearAlmacen('clientes', 'id');

// Al cerrar sesión no queda nada en memoria (puede entrar otra persona con
// otros permisos en la misma computadora).
onAuthStateChanged(auth, (user) => {
  if (user) return;
  almacenes.forEach((a) => { a.detener(); a.avisar(); });
});

function useAlmacen(almacen, orden) {
  const estado = useSyncExternalStore(almacen.suscribir, almacen.leer);
  return { lista: orden ? almacen.ordenadaPor(orden) : estado.lista, cargando: estado.cargando };
}

// IMPORTANTE: las listas y sus objetos son compartidos entre pantallas -- no
// modificarlos (ni ordenarlos con .sort() directo: usar [...lista].sort()).

// Catálogo completo en vivo. Cada producto trae `sku` (id del documento).
export function useProductos({ orden } = {}) {
  const { lista, cargando } = useAlmacen(almacenProductos, orden);
  return { productos: lista, cargando };
}

// Publicaciones de Mercado Libre en vivo. Cada una trae `id`.
export function usePublicacionesML({ orden } = {}) {
  const { lista, cargando } = useAlmacen(almacenPublicacionesML, orden);
  return { publicaciones: lista, cargando };
}

// Clientes en vivo. Cada uno trae `id`.
// Las fichas repetidas que se unieron a otra (fusionado_en) no se muestran:
// sus puntos y compras ya están en la ficha principal.
const clientesVisibles = new WeakMap();
export function useClientes() {
  const { lista, cargando } = useAlmacen(almacenClientes);
  let visibles = clientesVisibles.get(lista);
  if (!visibles) {
    visibles = lista.filter((c) => !c.fusionado_en);
    clientesVisibles.set(lista, visibles);
  }
  return { clientes: visibles, cargando };
}

export const obtenerProductos = () => almacenProductos.unaVez();
export const obtenerPublicacionesML = () => almacenPublicacionesML.unaVez();

// Empieza a traer el catálogo apenas se entra al sistema, así cuando se abre
// Stock, Consultar precios o el POS ya está listo.
export function precalentarProductos() {
  almacenProductos.suscribir(() => {})();
}
